import { useCallback } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { ViewHeader } from './DashboardLayout';
import type { SettingsResponse } from './types';
import { SettingsCards, SettingsStates } from '../components/SettingsCards';

/**
 * Dashboard → Settings.
 *
 * Thin dashboard wrapper around the shared sync-settings card set
 * (`components/SettingsCards`). The cards + toggle plumbing live in the shared
 * component so the onboarding wizard (M14 QA #18) reuses the SAME config
 * surface — one component, two entry points (here + the wizard's "Sync
 * settings" step).
 *
 * Reference: docs/PHASE2-PLAN.md M14 (QA #18 — Settings reuse)
 *            src/handlers/dashboard.ts (handleSettingsGet/Update)
 *            db/global.sql (tenants DDL)
 */
export function Settings() {
  const fetcher = useCallback(
    (signal: AbortSignal) => api.get<SettingsResponse>('admin/settings', { signal }),
    []
  );
  const { data, loading, error, refetch } = useAsync(fetcher, []);

  return (
    <>
      <ViewHeader
        title="Settings"
        subtitle="Tenant-wide sync behavior. Changes take effect on the next webhook."
      />

      {(loading || error || !data) && (
        <SettingsStates loading={loading} error={error} onRetry={refetch} />
      )}

      {data && <SettingsCards settings={data} onChanged={refetch} />}
    </>
  );
}
